import { useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

import MyTable from "../../../components/MyTable";
import { getStatusLabel } from "../../../utils";
import { useNavigate } from "react-router-dom";
import { useBoardItem } from "../../../hooks/useBoardItem";
import {
  BOARD_PERMISSION,
  NON_MEMBER_PERMISSIONS,
} from "../../../constants/permission";
import {
  checkRoleAndPermissions,
  getPermissionMessage,
} from "../../../components/CheckRoleAndPermission";
import { AuthContext } from "../../../context/AuthProvider";
import MyDropdownSelect from "../../../components/inputs/MyDropdownSelect";
import { SiteContext } from "../../../context/SiteProvider";
import { POST_STATUS_IDEA } from "../../../constants";

const ResearchList03 = ({ boardCode }) => {
  const navigate = useNavigate();
  const { useGetBoardItems, searchBoardItemsMutation, useGetBoard } =
    useBoardItem();
  const { roleCode, isProfileLoading, isAdmin } = useContext(AuthContext);

  const { isMobile } = useContext(SiteContext);

  const headers = [
    {
      id: "title",
      title: "제목",
      style: {
        width: 450,
        minWidth: 250,
        textAlign: "center",
      },
    },
    {
      id: "author",
      title: "작성자",
      style: {
        width: 120,
        textAlign: "center",
      },
    },
    {
      id: "createdDate",
      title: "제출일",
      style: {
        width: 100,
        minWidth: 100,
        textAlign: "center",
      },
    },
    {
      id: "replyDate",
      title: "답변일",
      style: {
        width: 100,
        minWidth: 100,
        textAlign: "center",
      },
    },
    {
      id: "status",
      title: "상태",
      style: {
        width: 60,
        textAlign: "center",
      },
    },
  ];

  const keySearchOptions = [
    {
      value: "title",
      label: "제목",
    },
    {
      value: "body",
      label: "내용",
    },
    {
      value: "writerName",
      label: "작성자",
    },
  ];

  const nonPermissions = NON_MEMBER_PERMISSIONS[boardCode];

  const [page, setPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [content, setContent] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [total, setTotal] = useState(0);
  const [keySearch, setKeySearch] = useState(keySearchOptions[0].value);

  const { data: board } = useGetBoard({ boardCode }, (data) => data.data);

  const { data, isLoading } = useGetBoardItems({
    size: board?.pageSize,
    page,
    sort: "createdDate,Desc",
    boardCode: board?.boardCode,
    enabled: checkRoleAndPermissions(
      roleCode,
      board,
      BOARD_PERMISSION.ACCESS,
      nonPermissions,
      isAdmin
    ),
  });

  const paginationProps = useMemo(
    () => ({
      size: board?.pageSize,
      page,
      setPage,
      maxPage,
      total,
    }),
    [board?.pageSize, maxPage, page, total]
  );
  const searchProps = useMemo(
    () => ({
      additionalFilter: (
        <div className="md:w-[130px] w-[70px]">
          {isMobile ? (
            <MyDropdownSelect
              outlined={false}
              defaultValue={keySearch}
              name="keySearch"
              options={keySearchOptions}
              onChange={setKeySearch}
              className="w-full"
              style={{ paddingLeft: 0, paddingRight: 0 }}
            />
          ) : (
            <MyDropdownSelect
              outlined={false}
              defaultValue={keySearch}
              name="keySearch"
              options={keySearchOptions}
              onChange={setKeySearch}
              className="w-full px-2"
            />
          )}
        </div>
      ),
      keyword,
      setKeyword: (val) => {
        setKeyword(val);
        if (val !== keyword) {
          searchData(val);
        }
      },
      btnClassName: "flex-1 max-w-[210px] text-lg",
      btnFontSize: "large",
      onClickAdd: () =>
        checkRoleAndPermissions(
          roleCode,
          board,
          BOARD_PERMISSION.WRITE,
          nonPermissions,
          isAdmin
        )
          ? navigate("/Research/Idea", { state: { isWriteMode: true } })
          : alert(getPermissionMessage(BOARD_PERMISSION.WRITE)),
    }),
    // eslint-disable-next-line
    [keySearch, keyword, isMobile, roleCode, board, isAdmin]
  );

  const mapData = (listData) => {
    setMaxPage(listData.totalPages);
    setTotal(listData.totalElements);

    const newContent = listData.content?.map((item) => {
      const newTitle = (
        <p
          className="overflow-hidden text-ellipsis line-clamp-1 text-gray-dark text-left md:text-lg sm:text-base text-sm"
          style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            wordBreak: "break-all",
          }}
        >
          {item.title}
        </p>
      );

      return {
        ...item,
        title: newTitle,
        createdDate: dayjs(item.createdDate).format("YYYY-MM-DD"),
        author: item.writer?.name,
        replyDate: item?.createdDateOfLastComment
          ? dayjs(item.createdDateOfLastComment).format("YYYY-MM-DD")
          : "-",
        status: getStatusLabel(
          item.additionalItems?.status,
          "md:text-lg sm:text-base text-sm",
          POST_STATUS_IDEA
        ),
      };
    });

    setContent(newContent);
  };
  const searchData = (val) => {
    if (
      checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.ACCESS,
        nonPermissions,
        isAdmin
      )
    ) {
      searchBoardItemsMutation.mutate(
        {
          page,
          size: board?.pageSize,
          boardCode: board?.boardCode,
          sort: "createdDate,Desc",
          keySearch: [keySearch],
          keyword: val,
        },
        {
          onSuccess: (res) => {
            mapData(res);
          },
        }
      );
    } else {
      alert(getPermissionMessage(BOARD_PERMISSION.ACCESS));
    }
  };
  useEffect(() => {
    if (!isLoading && data?.data) {
      mapData(data.data);
    }
  }, [isLoading, data]);

  return !isProfileLoading ? (
    <div className="pt-5">
      <MyTable
        showIndex
        canClick
        showAddBtn={checkRoleAndPermissions(
          roleCode,
          board,
          BOARD_PERMISSION.WRITE,
          nonPermissions,
          isAdmin
        )}
        headers={headers}
        data={content}
        dataKey="itemNo"
        searchProps={searchProps}
        loading={isLoading}
        paginationProps={paginationProps}
        addText="연구 과제 제안하기"
        onRowClick={(row) => {
          if (
            checkRoleAndPermissions(
              roleCode,
              board,
              BOARD_PERMISSION.READ,
              nonPermissions,
              isAdmin
            )
          ) {
            navigate(`/Research/Idea/${row.itemNo}`);
          } else {
            alert(getPermissionMessage(BOARD_PERMISSION.READ));
          }
        }}
      />
    </div>
  ) : null;
};

export default ResearchList03;
